import React, { useEffect, useState } from "react";
import "./EMPLeaveHistory.css";
import { getGender } from "../utils/cryptoUtils";
import apiClient from "../utils/apiClient";
import SessionHandler from "../components/SessionHandler";

const LeaveHistory = () => {
  const [requests, setRequests] = useState([]);
  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState("summary");

  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const gender = getGender();

  const leaveTypes = [
    "Casual Leave",
    "Sick Leave",
    "Earned Leave",
    "Loss Of Pay",
    "Paternity Leave",
    "Maternity Leave",
    "Wedding Leave",
  ];

  /* ================= Helpers ================= */
  const calculateDays = (leave) => {
    if (leave.is_half_day || leave.half_day === true) return 0.5;
    if (leave.days_count) return Number(leave.days_count);

    const start = new Date(leave.start_date);
    const end = new Date(leave.end_date);
    return Math.max(
      Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1,
      1
    );
  };

  const parse = (value) => {
    if (!value) return { used: 0, total: 0, remaining: 0 };
    const [used, total] = value.split(" / ").map(Number);
    return { used, total, remaining: Math.max(total - used, 0) };
  };

  const totalFromSummary = (key) => {
    if (!summary?.total?.[key]) return { used: 0, total: 0, remaining: 0 };
    return parse(summary.total[key]);
  };

  /* ================= Fetch APIs ================= */
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      setError("");

      try {
        const history = await apiClient(
          "/leaves/self/"
        );

        const list = Array.isArray(history) ? history : [];
        const formatted = list.map((l) => ({
          ...l,
          days: calculateDays(l),
        }));

        setRequests(formatted);

        const summaryData = await apiClient(
          "/leaves/summary/"
        );
        setSummary(summaryData);
      } catch (err) {
        console.error(err);
        setError("Failed to load leave data");
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  /* ================= Filters ================= */
  const filteredRequests = requests.filter(
    (leave) =>
      (statusFilter === "" || leave.status === statusFilter) &&
      (typeFilter === "" || leave.leave_type === typeFilter)
  );

  /* ================= UI ================= */
  return (
    <div className="leave-history-container">
      <h2>My Leaves</h2>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={activeTab === "summary" ? "active" : ""}
          onClick={() => setActiveTab("summary")}
        >
          Summary
        </button>
        <button
          className={activeTab === "remaining" ? "active" : ""}
          onClick={() => setActiveTab("remaining")}
        >
          Remaining
        </button>
        <button
          className={activeTab === "history" ? "active" : ""}
          onClick={() => setActiveTab("history")}
        >
          History
        </button>
      </div>

      {/* ===== Summary ===== */}
      {activeTab === "summary" && summary && (
        <div className="leave-summary-tiles center">
          <div className="modern-tile">
            <h3>Casual Leave</h3>
            <p>{totalFromSummary("casual_leave").used} used</p>
          </div>
          <div className="modern-tile">
            <h3>Sick Leave</h3>
            <p>{totalFromSummary("sick_leave").used} used</p>
          </div>
          <div className="modern-tile">
            <h3>Earned Leave</h3>
            <p>{totalFromSummary("earned_leave").used} used</p>
          </div>
        </div>
      )}

      {/* ===== Remaining ===== */}
      {activeTab === "remaining" && summary && (
        <div className="leave-summary-tiles center">
          <div className="modern-tile">
            <h3>Casual Leave</h3>
            <p>{totalFromSummary("casual_leave").remaining} left</p>
          </div>
          <div className="modern-tile">
            <h3>Sick Leave</h3>
            <p>{totalFromSummary("sick_leave").remaining} left</p>
          </div>
          <div className="modern-tile">
            <h3>Earned Leave</h3>
            <p>{totalFromSummary("earned_leave").remaining} left</p>
          </div>
        </div>
      )}

      {/* ===== History ===== */}
      {activeTab === "history" && (
        <>
          <div className="filter-controls">
            <select onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>

            <select onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">All Types</option>
              {leaveTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {loading ? (
            <p>Loading...</p>
          ) : error ? (
            <p className="error">{error}</p>
          ) : filteredRequests.length === 0 ? (
            <p className="no-records">No leave records found.</p>
          ) : (
            <table className="leave-table">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Type</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Days</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((leave) => (
                  <tr key={leave.id}>
                    <td>{leave.subject}</td>
                    <td>
                      {leave.is_half_day
                        ? `${leave.leave_type} (Half)`
                        : leave.leave_type}
                    </td>
                    <td>{leave.start_date}</td>
                    <td>{leave.end_date}</td>
                    <td>{leave.days}</td>
                    <td className={`status ${leave.status.toLowerCase()}`}>
                      {leave.status}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      <SessionHandler />
    </div>
  );
};

export default LeaveHistory;
